import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Car, 
  Plus, 
  Pencil, 
  Trash2, 
  User, 
  Calendar,
  Gauge,
  Fuel,
  Settings,
  LogOut,
  Save,
  MapPin,
  Phone,
  Mail,
  FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MeineGaragePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, logout, updateProfile } = useAuth();
  
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    marke: "",
    modell: "",
    kennzeichen: "",
    baujahr: "",
    erstzulassung: "",
    kilometerstand: "",
    kraftstoff: "",
    getriebe: "",
    leistung_ps: "",
    hubraum: "",
    farbe: "",
    tueren: "",
    fahrzeugtyp: "",
    vin: "",
    naechste_hu: "",
    letzte_inspektion: "",
    notizen: ""
  });
  
  const [profileForm, setProfileForm] = useState({
    name: "",
    telefon: "",
    adresse: "",
    plz: "",
    ort: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Load user data into profile form
  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || "",
        telefon: user.telefon || "",
        adresse: user.adresse || "",
        plz: user.plz || "",
        ort: user.ort || ""
      });
    }
  }, [user]);

  // Load vehicles
  useEffect(() => {
    if (isAuthenticated) {
      loadVehicles();
    }
  }, [isAuthenticated]);

  const loadVehicles = async () => {
    setLoadingVehicles(true);
    try {
      const response = await axios.get(`${API}/user/vehicles`, { withCredentials: true });
      setVehicles(response.data);
    } catch (error) {
      console.error("Error loading vehicles:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoadingVehicles(false);
    }
  };

  const resetVehicleForm = () => {
    setVehicleForm({
      marke: "",
      modell: "",
      kennzeichen: "",
      baujahr: "",
      erstzulassung: "",
      kilometerstand: "",
      kraftstoff: "",
      getriebe: "",
      leistung_ps: "",
      hubraum: "",
      farbe: "",
      tueren: "",
      fahrzeugtyp: "",
      vin: "",
      naechste_hu: "",
      letzte_inspektion: "",
      notizen: ""
    });
    setEditingVehicle(null);
  };

  const handleVehicleSubmit = async () => {
    if (!vehicleForm.marke || !vehicleForm.modell || !vehicleForm.kennzeichen) {
      toast.error("Bitte Marke, Modell und Kennzeichen ausfüllen");
      return;
    }

    try {
      const data = {
        ...vehicleForm,
        baujahr: vehicleForm.baujahr ? parseInt(vehicleForm.baujahr) : null,
        kilometerstand: vehicleForm.kilometerstand ? parseInt(vehicleForm.kilometerstand) : null,
        leistung_ps: vehicleForm.leistung_ps ? parseInt(vehicleForm.leistung_ps) : null,
        hubraum: vehicleForm.hubraum ? parseInt(vehicleForm.hubraum) : null,
        tueren: vehicleForm.tueren ? parseInt(vehicleForm.tueren) : null
      };

      if (editingVehicle) {
        await axios.put(`${API}/user/vehicles/${editingVehicle.id}`, data, { withCredentials: true });
        toast.success("Fahrzeug aktualisiert");
      } else {
        await axios.post(`${API}/user/vehicles`, data, { withCredentials: true });
        toast.success("Fahrzeug hinzugefügt");
      }
      
      setVehicleDialog(false);
      resetVehicleForm();
      loadVehicles();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const editVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      marke: vehicle.marke || "",
      modell: vehicle.modell || "",
      kennzeichen: vehicle.kennzeichen || "",
      baujahr: vehicle.baujahr?.toString() || "",
      erstzulassung: vehicle.erstzulassung || "",
      kilometerstand: vehicle.kilometerstand?.toString() || "",
      kraftstoff: vehicle.kraftstoff || "",
      getriebe: vehicle.getriebe || "",
      leistung_ps: vehicle.leistung_ps?.toString() || "",
      hubraum: vehicle.hubraum?.toString() || "",
      farbe: vehicle.farbe || "",
      tueren: vehicle.tueren?.toString() || "",
      fahrzeugtyp: vehicle.fahrzeugtyp || "",
      vin: vehicle.vin || "",
      naechste_hu: vehicle.naechste_hu || "",
      letzte_inspektion: vehicle.letzte_inspektion || "",
      notizen: vehicle.notizen || ""
    });
    setVehicleDialog(true);
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm("Möchten Sie dieses Fahrzeug wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/user/vehicles/${vehicleId}`, { withCredentials: true });
      toast.success("Fahrzeug gelöscht");
      loadVehicles();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      await updateProfile(profileForm);
      toast.success("Profil gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Erfolgreich abgemeldet");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#EFDF00] border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-1 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Header */}
          <div className="bg-gradient-to-r from-black to-gray-800 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="garage-welcome-title">
                  Willkommen, {user?.name?.split(" ")[0]}!
                </h1>
                <p className="text-gray-300 mt-1">
                  Verwalten Sie hier Ihre Fahrzeuge und Daten
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-black"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>

          <Tabs defaultValue="garage" className="space-y-6">
            <TabsList className="bg-white border">
              <TabsTrigger value="garage" className="data-[state=active]:bg-[#EFDF00]">
                <Car className="w-4 h-4 mr-2" />
                Meine Garage
              </TabsTrigger>
              <TabsTrigger value="profil" className="data-[state=active]:bg-[#EFDF00]">
                <User className="w-4 h-4 mr-2" />
                Mein Profil
              </TabsTrigger>
            </TabsList>

            {/* Garage Tab */}
            <TabsContent value="garage">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Meine Fahrzeuge</CardTitle>
                    <CardDescription>
                      Fügen Sie Ihre Fahrzeuge hinzu, um Termine schneller zu buchen
                    </CardDescription>
                  </div>
                  <Dialog open={vehicleDialog} onOpenChange={(open) => {
                    setVehicleDialog(open);
                    if (!open) resetVehicleForm();
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#EFDF00] text-black hover:bg-yellow-400" data-testid="add-vehicle-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Fahrzeug hinzufügen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>
                          {editingVehicle ? "Fahrzeug bearbeiten" : "Neues Fahrzeug hinzufügen"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {/* Pflichtfelder */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Marke *</Label>
                            <Input
                              value={vehicleForm.marke}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, marke: e.target.value }))}
                              placeholder="z.B. Renault"
                              data-testid="vehicle-marke"
                            />
                          </div>
                          <div>
                            <Label>Modell *</Label>
                            <Input
                              value={vehicleForm.modell}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, modell: e.target.value }))}
                              placeholder="z.B. Clio"
                              data-testid="vehicle-modell"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Kennzeichen *</Label>
                            <Input
                              value={vehicleForm.kennzeichen}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, kennzeichen: e.target.value.toUpperCase() }))}
                              placeholder="z.B. AB-CD 1234"
                              data-testid="vehicle-kennzeichen"
                            />
                          </div>
                          <div>
                            <Label>Baujahr</Label>
                            <Input
                              type="number"
                              value={vehicleForm.baujahr}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, baujahr: e.target.value }))}
                              placeholder="z.B. 2020"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Erstzulassung</Label>
                            <Input
                              type="date"
                              value={vehicleForm.erstzulassung}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, erstzulassung: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label>Kilometerstand</Label>
                            <Input
                              type="number"
                              value={vehicleForm.kilometerstand}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, kilometerstand: e.target.value }))}
                              placeholder="z.B. 50000"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Kraftstoff</Label>
                            <Select
                              value={vehicleForm.kraftstoff}
                              onValueChange={(value) => setVehicleForm(prev => ({ ...prev, kraftstoff: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Benzin">Benzin</SelectItem>
                                <SelectItem value="Diesel">Diesel</SelectItem>
                                <SelectItem value="Elektro">Elektro</SelectItem>
                                <SelectItem value="Hybrid">Hybrid</SelectItem>
                                <SelectItem value="Plugin-Hybrid">Plugin-Hybrid</SelectItem>
                                <SelectItem value="Gas (LPG)">Gas (LPG)</SelectItem>
                                <SelectItem value="Gas (CNG)">Gas (CNG)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Getriebe</Label>
                            <Select
                              value={vehicleForm.getriebe}
                              onValueChange={(value) => setVehicleForm(prev => ({ ...prev, getriebe: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Schaltgetriebe">Schaltgetriebe</SelectItem>
                                <SelectItem value="Automatik">Automatik</SelectItem>
                                <SelectItem value="Halbautomatik">Halbautomatik</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Leistung (PS)</Label>
                            <Input
                              type="number"
                              value={vehicleForm.leistung_ps}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, leistung_ps: e.target.value }))}
                              placeholder="z.B. 90"
                            />
                          </div>
                          <div>
                            <Label>Hubraum (ccm)</Label>
                            <Input
                              type="number"
                              value={vehicleForm.hubraum}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, hubraum: e.target.value }))}
                              placeholder="z.B. 1200"
                            />
                          </div>
                          <div>
                            <Label>Türen</Label>
                            <Select
                              value={vehicleForm.tueren}
                              onValueChange={(value) => setVehicleForm(prev => ({ ...prev, tueren: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="2">2</SelectItem>
                                <SelectItem value="3">3</SelectItem>
                                <SelectItem value="4">4</SelectItem>
                                <SelectItem value="5">5</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Farbe</Label>
                            <Input
                              value={vehicleForm.farbe}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, farbe: e.target.value }))}
                              placeholder="z.B. Schwarz Metallic"
                            />
                          </div>
                          <div>
                            <Label>Fahrzeugtyp</Label>
                            <Select
                              value={vehicleForm.fahrzeugtyp}
                              onValueChange={(value) => setVehicleForm(prev => ({ ...prev, fahrzeugtyp: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Limousine">Limousine</SelectItem>
                                <SelectItem value="Kombi">Kombi</SelectItem>
                                <SelectItem value="SUV">SUV</SelectItem>
                                <SelectItem value="Kleinwagen">Kleinwagen</SelectItem>
                                <SelectItem value="Van">Van</SelectItem>
                                <SelectItem value="Cabrio">Cabrio</SelectItem>
                                <SelectItem value="Coupe">Coupe</SelectItem>
                                <SelectItem value="Transporter">Transporter</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label>Fahrgestellnummer (VIN)</Label>
                          <Input
                            value={vehicleForm.vin}
                            onChange={(e) => setVehicleForm(prev => ({ ...prev, vin: e.target.value.toUpperCase() }))}
                            placeholder="17-stellige VIN"
                            maxLength={17}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Nächste HU/AU</Label>
                            <Input
                              type="date"
                              value={vehicleForm.naechste_hu}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, naechste_hu: e.target.value }))}
                            />
                          </div>
                          <div>
                            <Label>Letzte Inspektion</Label>
                            <Input
                              type="date"
                              value={vehicleForm.letzte_inspektion}
                              onChange={(e) => setVehicleForm(prev => ({ ...prev, letzte_inspektion: e.target.value }))}
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Notizen</Label>
                          <Textarea
                            value={vehicleForm.notizen}
                            onChange={(e) => setVehicleForm(prev => ({ ...prev, notizen: e.target.value }))}
                            placeholder="Zusätzliche Informationen..."
                            rows={3}
                          />
                        </div>

                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => setVehicleDialog(false)}>
                            Abbrechen
                          </Button>
                          <Button 
                            className="bg-[#EFDF00] text-black hover:bg-yellow-400"
                            onClick={handleVehicleSubmit}
                            data-testid="save-vehicle-btn"
                          >
                            {editingVehicle ? "Speichern" : "Hinzufügen"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {loadingVehicles ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#EFDF00] border-t-transparent"></div>
                    </div>
                  ) : vehicles.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Car className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium">Keine Fahrzeuge in Ihrer Garage</p>
                      <p className="text-sm">Fügen Sie Ihr erstes Fahrzeug hinzu!</p>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {vehicles.map((vehicle) => (
                        <div 
                          key={vehicle.id}
                          className="border rounded-xl p-4 hover:shadow-md transition-shadow"
                          data-testid={`vehicle-card-${vehicle.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-[#EFDF00] rounded-lg flex items-center justify-center">
                                <Car className="w-6 h-6 text-black" />
                              </div>
                              <div>
                                <h3 className="font-bold">{vehicle.marke} {vehicle.modell}</h3>
                                <p className="text-sm text-gray-600 font-mono">{vehicle.kennzeichen}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => editVehicle(vehicle)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => deleteVehicle(vehicle.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                            {vehicle.baujahr && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Calendar className="w-4 h-4" />
                                <span>Baujahr {vehicle.baujahr}</span>
                              </div>
                            )}
                            {vehicle.kilometerstand && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Gauge className="w-4 h-4" />
                                <span>{vehicle.kilometerstand.toLocaleString()} km</span>
                              </div>
                            )}
                            {vehicle.kraftstoff && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Fuel className="w-4 h-4" />
                                <span>{vehicle.kraftstoff}</span>
                              </div>
                            )}
                            {vehicle.leistung_ps && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Settings className="w-4 h-4" />
                                <span>{vehicle.leistung_ps} PS</span>
                              </div>
                            )}
                          </div>

                          <div className="mt-4 pt-4 border-t">
                            <Link to={`/termin?fahrzeug=${vehicle.id}`}>
                              <Button className="w-full bg-black text-white hover:bg-gray-800" size="sm">
                                <Calendar className="w-4 h-4 mr-2" />
                                Termin buchen
                              </Button>
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Profil Tab */}
            <TabsContent value="profil">
              <Card>
                <CardHeader>
                  <CardTitle>Meine Daten</CardTitle>
                  <CardDescription>
                    Diese Daten werden automatisch in Formularen ausgefüllt
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="profile-name">Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="profile-name"
                          className="pl-10"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                          data-testid="profile-name"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-telefon">Telefon</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="profile-telefon"
                          className="pl-10"
                          value={profileForm.telefon}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, telefon: e.target.value }))}
                          data-testid="profile-telefon"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profile-email">E-Mail (nicht änderbar)</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="profile-email"
                        className="pl-10 bg-gray-100"
                        value={user?.email || ""}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profile-adresse">Straße & Hausnummer</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="profile-adresse"
                        className="pl-10"
                        value={profileForm.adresse}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, adresse: e.target.value }))}
                        placeholder="Musterstraße 123"
                        data-testid="profile-adresse"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="profile-plz">PLZ</Label>
                      <Input
                        id="profile-plz"
                        value={profileForm.plz}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, plz: e.target.value }))}
                        placeholder="12345"
                        data-testid="profile-plz"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-ort">Ort</Label>
                      <Input
                        id="profile-ort"
                        value={profileForm.ort}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, ort: e.target.value }))}
                        placeholder="Musterstadt"
                        data-testid="profile-ort"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button 
                      className="bg-[#EFDF00] text-black hover:bg-yellow-400"
                      onClick={handleProfileSave}
                      disabled={savingProfile}
                      data-testid="save-profile-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingProfile ? "Wird gespeichert..." : "Änderungen speichern"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MeineGaragePage;
